import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { SignOut, Plus, ListBullets, ArrowLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { QUOTE_TYPES } from "@/lib/dseQuotes";
import { isQuoteOnlyUser } from "@/lib/dseAccess";
import DseQuoteForm from "@/components/dse/DseQuoteForm";
import DseMyQuotes from "@/components/dse/DseMyQuotes";

export default function DsePage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("new");
  const [type, setType] = useState("ice");
  const [schemes, setSchemes] = useState([]);
  const perms = user?.permissions || [];
  const canQuote = user?.role === "admin" || perms.includes("quotes.create");

  useEffect(() => {
    if (!canQuote) return;
    api.get("/finance/schemes").then(({ data }) => {
      const list = Array.isArray(data) ? data : data.rows || [];
      setSchemes(list.filter((s) => s.active !== false));
    }).catch(() => {});
  }, [canQuote]);

  if (!canQuote) return <Navigate to="/" replace />;
  const scopeLabel = user.role === "admin" || perms.includes("branches.view_all") ? "all branches"
    : perms.includes("quotes.branch_view") || perms.includes("quotes.manage") ? "your branch" : "your quotes";

  return (
    <div className="min-h-screen bg-canvas pb-24" data-testid="dse-page">
      <header className="sticky top-0 z-20 bg-ink text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {!isQuoteOnlyUser(user) && (
            <a href="/" className="p-1 -ml-1" data-testid="dse-back-to-app" aria-label="Back to main app"><ArrowLeft size={18} /></a>
          )}
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-[0.3em] text-amber-300">TVS QUOTES</div>
            <div className="text-sm font-semibold truncate" data-testid="dse-user-name">{user.name}</div>
          </div>
        </div>
        <button type="button" onClick={logout} data-testid="dse-logout" className="p-2" aria-label="Sign out"><SignOut size={20} /></button>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-5">
        {!user.phone && (
          <div data-testid="dse-no-mobile-banner" className="mb-4 border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-ink">
            Your mobile number is not set, so quotes show the showroom phone. Ask the admin to add it in Users.
          </div>
        )}
        {tab === "new" ? (
          <>
            <div className="grid grid-cols-3 gap-2 mb-6" data-testid="dse-type-switch">
              {Object.entries(QUOTE_TYPES).map(([k, t]) => (
                <button key={k} type="button" onClick={() => setType(k)} data-testid={`dse-type-${k}`}
                  className={`py-3 px-2 border text-left transition-colors ${type === k ? "bg-ink text-white border-ink" : "bg-white border-canvas-line text-ink"}`}>
                  <div className="font-semibold text-sm">{t.label}</div>
                  <div className={`text-[10px] leading-tight ${type === k ? "text-white/60" : "text-ink-muted"}`}>{t.sub}</div>
                </button>
              ))}
            </div>
            <DseQuoteForm key={type} type={type} schemes={schemes} />
          </>
        ) : (
          <DseMyQuotes scopeLabel={scopeLabel} />
        )}
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-20 bg-white border-t border-canvas-line grid grid-cols-2">
        {[["new", "New quote", Plus], ["list", "My quotes", ListBullets]].map(([k, l, Icon]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`dse-tab-${k}`}
            className={`py-3 flex flex-col items-center gap-1 text-xs font-semibold transition-colors ${tab === k ? "text-ink" : "text-ink-muted"}`}>
            <Icon size={20} weight={tab === k ? "bold" : "regular"} />
            {l}
          </button>
        ))}
      </nav>
    </div>
  );
}
